import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from './components/header';
import Footer from './components/footer';
import { useNavigate } from 'react-router-dom';

const WhyIFLO = () => {
  const navigate = useNavigate();
  const benefits = [
    {
      title: "Proactive Prevention",
      description: "iFLO Pro's bio-enzymatic formula continuously cleans A/C drain lines, preventing the buildup of Zooglea, dirt, and debris that cause clogs.",
      icon: "🛡️",
      color: "white"
    },
    {
      title: "Reduce Callbacks",
      description: "Minimize emergency service calls related to drain line blockages, freeing up your technicians for other jobs.",
      icon: "📞",
      color: "white"
    },
    {
      title: "Potential for Recurring Revenue",
      description: "iFLO Pro is a valuable maintenance solution that can be part of and create more ongoing service agreements.",
      icon: "💲",
      color: "white"
    },
    {
      title: "Improve Customer Retention",
      description: "iFLO Pro keeps you in contact with your customers and gives you the ability to provide top-of-the-line service that will stand out against competition.",
      icon: "🤝",
      color: "white"
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="bg-white min-h-screen text-black"
    >
      <Helmet>
        <title>Why Choose iFLO Pro</title>
        <meta name="description" content="Discover why iFLO Pro is the leading solution for water leak detection and smart water monitoring for your property." />
        <meta name="keywords" content="iFLO Pro, water leak detection, smart water monitoring, prevent water damage" />
      </Helmet>

      <Header />

      <main>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative h-[300px] md:h-[500px] mt-12 mb-12"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-700"></div>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="absolute inset-0 flex flex-col justify-center items-center p-12 text-center"
          >
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="md:text-5xl text-3xl font-bold text-white mb-6"
            >
              Why Choose iFLO Pro?
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="text-xl text-gray-200 max-w-3xl"
            >
              The smart, automated solution that ensures smooth A/C operation and happier customers.
            </motion.p>
          </motion.div>
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 py-2">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl font-bold text-center mb-12"
            >
              See iFLO Pro in Action
            </motion.h2>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <video 
                className="w-full rounded-xl shadow-xl"
                src="https://cdn.shopify.com/videos/c/o/v/d5b7140a783f48bea90958383bb78f41.mp4" 
                title="iFLO Pro Demo"
                controls
                allowFullScreen
              />
            </motion.div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="grid md:grid-cols-2 gap-8 mb-16"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                whileHover={{ scale: 1.03 }}
                className={`${benefit.color} border-[1px] border-yellow-500 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              >
                <div className="flex items-start space-x-4">
                  <motion.span 
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-4xl"
                  >
                    {benefit.icon}
                  </motion.span>
                  <div>
                    <motion.h3 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      className="text-xl font-semibold text-gray-900 mb-2"
                    >
                      {benefit.title}
                    </motion.h3>
                    <motion.p 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      className="text-gray-600"
                    >
                      {benefit.description}
                    </motion.p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gray-200 rounded-xl p-8 shadow-lg mb-16"
          >
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              Trusted by Professionals
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-gray-700 mb-6 text-lg"
            >
              iFLO PRO is the preferred choice for property managers, homeowners, and HVAC professionals across the country. 
              Our reliable technology has made us the industry leader in preventing unnecessary service calls, frustrated clients, and spontaneous water leaks.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/signup')}
                className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300"
              >
                Get Started Today
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/about')}
                className="bg-white text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-300 border border-gray-200"
              >
                Learn More
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl font-bold mb-8"
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              {[
                {
                  question: "How does iFLO Pro detect water leaks?",
                  answer: "iFLO Pro uses advanced sensors that monitor water flow and pressure in your plumbing system. When abnormal patterns are detected, the system alerts you immediately through the mobile app."
                },
                {
                  question: "Is iFLO Pro difficult to install?",
                  answer: "Not at all! iFLO Pro is designed for easy DIY installation. Most customers can set up the system in under 30 minutes without professional help."
                },
                {
                  question: "Can iFLO Pro save me money?",
                  answer: "Absolutely. By detecting leaks early, iFLO Pro helps prevent costly water damage repairs. Many insurance companies also offer discounts for homes with leak detection systems."
                }
              ].map((faq, index) => (
                <motion.div 
                  key={index} 
                  variants={fadeIn}
                  transition={{ delay: index * 0.2 }}
                  className="mb-6 border-[1px] border-yellow-500 text-left bg-gray-50 rounded-lg p-6"
                >
                  <motion.h3 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="text-xl font-semibold mb-2"
                  >
                    {faq.question}
                  </motion.h3>
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-gray-600"
                  >
                    {faq.answer}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl font-bold mb-6"
            >
              Ready to step up your game?
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
            >
              Join thousands of certified installers who trust iFLO Pro to monitor customer systems for them.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/signup')}
              className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-3 rounded-full text-md font-semibold transition-colors duration-300">
              Get iFLO Pro Today
            </motion.button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </motion.div>
  );
};

export default WhyIFLO;
